import { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, ColorType, LineSeries } from 'lightweight-charts';
import axios from 'axios';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const COIN_COLORS = {
  'BTC-USD': '#f7931a',
  'ETH-USD': '#627eea',
  'SOL-USD': '#00FFA3',
  'LTC-USD': '#bfbbbb',
  'DOGE-USD': '#c2a633',
  'BCH-USD': '#8dc351',
  'XRP-USD': '#00AAE4',
  'SHIB-USD': '#ff5722',
};

const DURATIONS = [
  { label: '1H', minutes: 60 },
  { label: '4H', minutes: 240 },
  { label: '12H', minutes: 720 },
  { label: '24H', minutes: 1440 },
  { label: '7D', minutes: 10080 },
  { label: '30D', minutes: 43200 },
];

const MultiCoinOverlay = ({ height = 350, activePairs = [] }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesMapRef = useRef({});
  const [selectedPairs, setSelectedPairs] = useState(() => {
    const defaults = ['BTC-USD', 'ETH-USD'];
    return activePairs.length >= 2 ? activePairs.slice(0, 3) : defaults;
  });
  const [duration, setDuration] = useState(1440);
  const [loading, setLoading] = useState(false);
  const [crosshairData, setCrosshairData] = useState({});
  const allPairs = Object.keys(COIN_COLORS);

  const togglePair = (pair) => {
    setSelectedPairs(prev => {
      if (prev.includes(pair)) {
        if (prev.length <= 1) return prev;
        return prev.filter(p => p !== pair);
      }
      return [...prev, pair];
    });
  };

  const fetchAndRender = useCallback(async () => {
    if (!chartRef.current || selectedPairs.length === 0) return;
    setLoading(true);

    try {
      const responses = await Promise.all(
        selectedPairs.map(pair =>
          axios.get(`${API_V2}/market/history?pair=${pair}&minutes=${duration}`)
            .then(res => ({ pair, data: res.data.history || [] }))
            .catch(() => ({ pair, data: [] }))
        )
      );

      // Remove old series
      Object.keys(seriesMapRef.current).forEach(pair => {
        try { chartRef.current.removeSeries(seriesMapRef.current[pair]); } catch {}
      });
      seriesMapRef.current = {};

      // Process each pair: normalize to % change from first data point
      const allSeriesData = {};
      responses.forEach(({ pair, data }) => {
        if (!data || data.length < 2) return;
        const basePrice = data[0].close || data[0].price;
        if (!basePrice || basePrice <= 0) return;

        const points = [];
        const seen = new Set();
        data.forEach(point => {
          const price = point.close || point.price;
          if (!price) return;
          let ts;
          if (point.timestamp) {
            ts = Math.floor(new Date(point.timestamp).getTime() / 1000);
          } else {
            return;
          }
          if (seen.has(ts)) return;
          seen.add(ts);
          const pctChange = ((price - basePrice) / basePrice) * 100;
          points.push({ time: ts, value: pctChange, rawPrice: price });
        });

        points.sort((a, b) => a.time - b.time);
        if (points.length > 1) {
          allSeriesData[pair] = points;
        }
      });

      // Add series to chart
      Object.entries(allSeriesData).forEach(([pair, points]) => {
        const series = chartRef.current.addSeries(LineSeries, {
          color: COIN_COLORS[pair] || '#888',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
          title: pair.split('-')[0],
          crosshairMarkerVisible: true,
          crosshairMarkerRadius: 4,
          priceFormat: {
            type: 'custom',
            formatter: (val) => `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`,
          },
        });
        series.setData(points);
        seriesMapRef.current[pair] = series;
      });

      chartRef.current.timeScale().fitContent();
    } catch (err) {
      console.error('Multi-coin overlay error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPairs, duration]);

  // Create chart once
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { type: ColorType.Solid, color: '#0a0a0a' },
        textColor: '#8b949e',
        fontFamily: 'monospace',
      },
      grid: {
        vertLines: { color: '#111', style: 1 },
        horzLines: { color: '#111', style: 1 },
      },
      crosshair: {
        mode: 0,
        vertLine: { color: '#484f58', width: 1, style: 2, labelBackgroundColor: '#1a1a1a' },
        horzLine: { color: '#484f58', width: 1, style: 2, labelBackgroundColor: '#1a1a1a' },
      },
      rightPriceScale: {
        borderColor: '#222',
        scaleMargins: { top: 0.05, bottom: 0.05 },
        autoScale: true,
      },
      localization: {
        locale: 'en-US',
      },
      timeScale: {
        borderColor: '#222',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (timestamp) => {
          const date = new Date(timestamp * 1000);
          if (duration >= 10080) return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          if (duration >= 1440) return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        },
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
    });

    // Zero line
    chart.addSeries(LineSeries, {
      color: '#333',
      lineWidth: 1,
      lineStyle: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    }).setData([]);

    // Crosshair tooltip
    chart.subscribeCrosshairMove((param) => {
      if (!param.time) { setCrosshairData({}); return; }
      const info = {};
      Object.entries(seriesMapRef.current).forEach(([pair, series]) => {
        const val = param.seriesData.get(series);
        if (val) {
          info[pair] = { pct: val.value, price: val.rawPrice };
        }
      });
      setCrosshairData(info);
    });

    chartRef.current = chart;

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }
      seriesMapRef.current = {};
    };
  }, [height, duration]);

  // Fetch data when pairs or duration change
  useEffect(() => {
    fetchAndRender();
  }, [fetchAndRender]);

  return (
    <div className="relative bg-[#0a0a0a] rounded-lg overflow-hidden border border-[#003B00]" data-testid="multi-coin-overlay">
      {/* Header: Coin toggles + duration */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#222] flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          {allPairs.map(pair => {
            const coin = pair.split('-')[0];
            const active = selectedPairs.includes(pair);
            return (
              <button
                key={pair}
                data-testid={`overlay-toggle-${coin}`}
                onClick={() => togglePair(pair)}
                className={`px-2 py-0.5 text-[10px] font-bold font-mono border transition-all ${
                  active
                    ? 'border-current opacity-100'
                    : 'border-[#333] text-gray-600 opacity-50 hover:opacity-75'
                }`}
                style={active ? { color: COIN_COLORS[pair], borderColor: COIN_COLORS[pair] } : {}}
              >
                {coin}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1">
          {DURATIONS.map(d => (
            <button
              key={d.minutes}
              data-testid={`overlay-duration-${d.label}`}
              onClick={() => setDuration(d.minutes)}
              className={`px-2 py-0.5 text-[10px] font-mono border ${
                duration === d.minutes
                  ? 'border-[#00FF41] text-[#00FF41]'
                  : 'border-[#333] text-gray-600 hover:text-gray-400'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* Crosshair tooltip */}
      {Object.keys(crosshairData).length > 0 && (
        <div className="absolute top-12 left-3 z-10 bg-black/90 border border-[#333] px-3 py-2 text-xs font-mono space-y-0.5 pointer-events-none">
          {Object.entries(crosshairData).map(([pair, info]) => (
            <div key={pair} className="flex items-center gap-3">
              <span style={{ color: COIN_COLORS[pair] }} className="font-bold w-10">{pair.split('-')[0]}</span>
              <span className="text-gray-300 w-24 text-right">
                ${info.price < 0.01 ? info.price?.toFixed(6) : info.price < 1 ? info.price?.toFixed(4) : info.price?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </span>
              <span className={`w-16 text-right ${info.pct >= 0 ? 'text-[#26a69a]' : 'text-[#ef5350]'}`}>
                {info.pct >= 0 ? '+' : ''}{info.pct?.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Chart */}
      <div ref={chartContainerRef} className="w-full" style={{ minHeight: height }} data-testid="overlay-chart-container" />

      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
          <span className="text-[#00FF41] font-mono text-sm animate-pulse">Loading...</span>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 py-1.5 text-[10px] text-gray-500 border-t border-[#222]">
        <span>% Change from period start</span>
        <span className="text-[#26a69a]">+ gain</span>
        <span className="text-[#ef5350]">- loss</span>
      </div>
    </div>
  );
};

export default MultiCoinOverlay;
